import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useSeo from "@/hooks/useSeo";
import { SectionBadge } from "@/components/Comparison";
import { AMOUNT_BUCKETS } from "@/data/seoContent";
import { ArrowRight, Check, PoundSterling } from "lucide-react";

export default function AmountLanding() {
  const { amount } = useParams();
  const b = AMOUNT_BUCKETS.find((x) => x.slug === amount);
  const title = b ? `${b.label} Business Loans · UK · SwiftCash` : "Business Loans · SwiftCash";
  const description = b
    ? `${b.label} business loans in the UK — indicative terms in hours, ${b.term} typical. Working capital, asset finance, commercial mortgage & more.`
    : "UK business loans from £5k to £500M.";
  useSeo({ title, description, path: `/business-loans/${amount || ""}` });
  if (!b) return <Navigate to="/" replace />;

  return (
    <div className="bg-white text-navy min-h-screen">
      <Navbar />
      <section className="hero-grad py-14 md:py-20">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-soft text-emerald-3 font-mono text-[10.5px] uppercase tracking-[0.2em] font-bold mb-5">
            <PoundSterling className="h-3 w-3" strokeWidth={2.6} /> UK business loans by amount
          </div>
          <h1 className="font-display font-extrabold text-[48px] md:text-[72px] leading-[1] tracking-[-0.04em] text-navy max-w-4xl">
            {b.label} business loans <br /><span className="text-emerald">in the UK.</span>
          </h1>
          <p className="mt-6 text-[17px] text-navy/70 max-w-2xl font-medium leading-[1.6]">
            Whole-of-market panel priced specifically for {b.range} facilities. Typical term <b className="text-navy">{b.term}</b>. Indicative offer in hours — no impact on the business credit file.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to={`/apply?amount=${b.amount}`} data-testid="amount-apply"
              className="btn-shine group inline-flex items-center gap-2 h-14 px-8 rounded-xl bg-emerald text-white font-extrabold text-[15px] hover:bg-emerald-2 shadow-emerald">
              Apply for {b.label} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.4} />
            </Link>
            <Link to="/#pricing" className="inline-flex items-center gap-2 h-14 px-7 rounded-xl border border-navy/15 text-navy font-bold text-[14px] hover:bg-paper-warm">See indicative pricing</Link>
          </div>
        </div>
      </section>
      <section className="py-16 md:py-20 bg-paper-warm">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <SectionBadge>Best-fit products</SectionBadge>
          <h2 className="font-display font-extrabold text-[32px] md:text-[44px] leading-[1.05] tracking-[-0.035em] text-navy mt-5 mb-10">
            Products that typically fund <span className="text-emerald">{b.label}</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-3">
            {b.products.map((p, i) => (
              <div key={i} className="rounded-xl bg-white border border-line p-5 flex items-start gap-3" data-testid={`amount-product-${i}`}>
                <span className="h-6 w-6 rounded-full bg-emerald flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="h-3.5 w-3.5 text-white" strokeWidth={3.2} />
                </span>
                <span className="text-[14.5px] text-navy font-bold leading-[1.5]">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-14 bg-white">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <div className="text-center mb-8">
            <div className="font-mono text-[10.5px] uppercase tracking-[0.22em] font-bold text-navy/50">Also popular</div>
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            {AMOUNT_BUCKETS.filter(x => x.slug !== b.slug).map((o) => (
              <Link key={o.slug} to={`/business-loans/${o.slug}`} data-testid={`amount-link-${o.slug}`}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-line text-navy hover:border-navy/30 hover:bg-paper-warm text-[13px] font-bold">
                <PoundSterling className="h-3 w-3" strokeWidth={2.4} /> {o.label} loans
              </Link>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
